import { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast, Toaster } from "sonner";
import { Search, Phone, MessageCircle, Filter, ShoppingCart, Wrench, ChevronDown, Globe, X, MapPin, Tag, Star, ArrowRight, Package, Truck, Clock, CalendarCheck, Camera, ChevronLeft, ChevronRight, Info, Settings, FileText, CheckCircle, XCircle, Shield, Copy, Check, Mail, SlidersHorizontal, ArrowUpDown, Eye, Instagram, Share2, Gavel, Calendar, Play, Video, ShieldCheck, User } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { usePersistedState, useScrollRestore } from "../hooks/useScrollRestore";
import { useSEO, JsonLdOrganization } from "../components/SEO";
import { MechanicalChecklistDisplay, calculateAverageScore, getOverallCondition } from "../components/MechanicalChecklist";
import { AIChatWidget } from "../components/AIChatWidget";
import { RecentlyViewed, getRecentIds, addRecentId } from "../components/boutique/RecentlyViewed";
import { isBlackFriday, DISPLAY, UrgencyMarquee, BFHero, CategoryDiscountTiles, BFLifestyle } from "../components/boutique/BlackFriday";

const API = process.env.REACT_APP_BACKEND_URL || '';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_509254e3-4fbd-4a68-8194-b9dcfb6ff3e4/artifacts/bylh1er6_Image.jpg";
const WHATSAPP = "+40792250058";
const PHONE = "0792250058";
const EMAIL = "office@scutersh.ro";

const TRANSLATIONS = {
  ro: {
    hero_title: "Scutere & Piese",
    hero_subtitle: "Calitate garantata, preturi imbatabile",
    search_placeholder: "Cauta scuter sau piesa...",
    tab_scooters: "Scutere",
    tab_parts: "Piese",
    tab_casse: "Dezmembrari",
    filter_brand: "Marca",
    filter_all: "Toate",
    filter_price: "Pret",
    filter_year: "An",
    filter_cc: "Cilindree",
    filter_type: "Tip",
    filter_category: "Categorie",
    filter_reset: "Reseteaza filtre",
    btn_order: "Comanda",
    btn_reserve: "Rezerva",
    btn_contact: "Contact WhatsApp",
    btn_details: "Detalii",
    btn_request_part: "Cere piesa",
    order_title: "Plasati comanda",
    order_first_name: "Prenume",
    order_last_name: "Nume",
    order_phone: "Telefon",
    order_email: "Email (optional)",
    order_address: "Adresa de livrare",
    order_city: "Oras",
    order_notes: "Note / Observatii",
    order_submit: "Trimite comanda",
    order_success: "Comanda inregistrata cu succes!",
    request_title: "Cerere piesa",
    request_brand: "Marca scuter",
    request_model: "Model scuter",
    request_year: "An fabricatie",
    request_part: "Descriere piesa cautata",
    request_submit: "Trimite cererea",
    request_success: "Cererea a fost trimisa! Va contactam in curand.",
    reserve_title: "Rezerva scuterul",
    reserve_submit: "Confirma rezervarea",
    reserve_success: "Scuterul a fost rezervat!",
    casse_title: "Dezmembrari Scutere",
    casse_subtitle: "Ceri piesa, noi o gasim! Scrie-ne ce ai nevoie.",
    casse_no_models: "Momentan nu avem modele pentru dezmembrare. Dar poti trimite o cerere!",
    price_from: "de la",
    km: "km",
    cc: "cc",
    year_label: "An",
    in_stock: "In stoc",
    reserved: "Rezervat",
    results: "rezultate",
    no_results: "Niciun rezultat gasit",
    free_gift: "CADOU",
    promo: "PROMOTIE",
    available: "disponibile",
    footer_text: "Scutersh - Scutere & Piese de Calitate",
    footer_address: "Bucuresti, Romania",
    appointment: "sau pe programare",
  },
  en: {
    hero_title: "Scooters & Parts",
    hero_subtitle: "Guaranteed quality, unbeatable prices",
    search_placeholder: "Search scooter or part...",
    tab_scooters: "Scooters",
    tab_parts: "Parts",
    tab_casse: "Dismantling",
    filter_brand: "Brand",
    filter_all: "All",
    filter_price: "Price",
    filter_year: "Year",
    filter_cc: "Engine",
    filter_type: "Type",
    filter_category: "Category",
    filter_reset: "Reset filters",
    btn_order: "Order",
    btn_reserve: "Reserve",
    btn_contact: "WhatsApp Contact",
    btn_details: "Details",
    btn_request_part: "Request Part",
    order_title: "Place Order",
    order_first_name: "First Name",
    order_last_name: "Last Name",
    order_phone: "Phone",
    order_email: "Email (optional)",
    order_address: "Delivery Address",
    order_city: "City",
    order_notes: "Notes",
    order_submit: "Submit Order",
    order_success: "Order placed successfully!",
    request_title: "Part Request",
    request_brand: "Scooter Brand",
    request_model: "Scooter Model",
    request_year: "Year",
    request_part: "Part Description",
    request_submit: "Submit Request",
    request_success: "Request sent! We'll contact you soon.",
    reserve_title: "Reserve this scooter",
    reserve_submit: "Confirm reservation",
    reserve_success: "Scooter reserved!",
    casse_title: "Scooter Dismantling",
    casse_subtitle: "Need a part? Tell us what you need, we'll find it!",
    casse_no_models: "No models currently available. But you can still send a request!",
    price_from: "from",
    km: "km",
    cc: "cc",
    year_label: "Year",
    in_stock: "In stock",
    reserved: "Reserved",
    results: "results",
    no_results: "No results found",
    free_gift: "FREE GIFT",
    promo: "PROMO",
    available: "available",
    footer_text: "Scutersh - Quality Scooters & Parts",
    footer_address: "Bucharest, Romania",
    appointment: "or by appointment",
  },
  fr: {
    hero_title: "Scooters & Pieces",
    hero_subtitle: "Qualite garantie, prix imbattables",
    search_placeholder: "Rechercher scooter ou piece...",
    tab_scooters: "Scooters",
    tab_parts: "Pieces",
    tab_casse: "Casse",
    filter_brand: "Marque",
    filter_all: "Toutes",
    filter_price: "Prix",
    filter_year: "Annee",
    filter_cc: "Cylindree",
    filter_type: "Type",
    filter_category: "Categorie",
    filter_reset: "Reinitialiser filtres",
    btn_order: "Commander",
    btn_reserve: "Reserver",
    btn_contact: "Contact WhatsApp",
    btn_details: "Details",
    btn_request_part: "Demander piece",
    order_title: "Passer commande",
    order_first_name: "Prenom",
    order_last_name: "Nom",
    order_phone: "Telephone",
    order_email: "Email (optionnel)",
    order_address: "Adresse de livraison",
    order_city: "Ville",
    order_notes: "Notes",
    order_submit: "Envoyer la commande",
    order_success: "Commande enregistree avec succes!",
    request_title: "Demande de piece",
    request_brand: "Marque scooter",
    request_model: "Modele scooter",
    request_year: "Annee",
    request_part: "Description de la piece",
    request_submit: "Envoyer la demande",
    request_success: "Demande envoyee! Nous vous contacterons bientot.",
    reserve_title: "Reserver ce scooter",
    reserve_submit: "Confirmer la reservation",
    reserve_success: "Scooter reserve!",
    casse_title: "Casse Scooters",
    casse_subtitle: "Besoin d'une piece? Dites-nous ce qu'il vous faut!",
    casse_no_models: "Aucun modele disponible actuellement. Mais vous pouvez envoyer une demande!",
    price_from: "a partir de",
    km: "km",
    cc: "cc",
    year_label: "Annee",
    in_stock: "En stock",
    reserved: "Reserve",
    results: "resultats",
    no_results: "Aucun resultat trouve",
    free_gift: "CADEAU",
    promo: "PROMO",
    available: "disponibles",
    footer_text: "Scutersh - Scooters & Pieces de Qualite",
    footer_address: "Bucarest, Roumanie",
    appointment: "ou sur rendez-vous",
  },
};

const PART_CATEGORIES = [
  { value: "moteur", ro: "Motor", en: "Engine", fr: "Moteur" },
  { value: "transmission", ro: "Transmisie", en: "Transmission", fr: "Transmission" },
  { value: "freins", ro: "Frane", en: "Brakes", fr: "Freins" },
  { value: "electrique", ro: "Electric", en: "Electrical", fr: "Electrique" },
  { value: "carrosserie", ro: "Caroserie", en: "Body", fr: "Carrosserie" },
  { value: "suspension", ro: "Suspensie", en: "Suspension", fr: "Suspension" },
  { value: "echappement", ro: "Esapament", en: "Exhaust", fr: "Echappement" },
  { value: "roues", ro: "Roti", en: "Wheels", fr: "Roues" },
  { value: "accessoires", ro: "Accesorii", en: "Accessories", fr: "Accessoires" },
  { value: "autres", ro: "Altele", en: "Other", fr: "Autres" },
];

const SCOOTER_TYPES = [
  { value: "fara_permis_25", ro: "Fara permis 25km/h", en: "No license 25km/h", fr: "Sans permis 25km/h", filter: s => s.scooter_type === "fara_permis" || (s.engine_cc <= 25) },
  { value: "cu_permis_50", ro: "Cu permis 50cc", en: "With license 50cc", fr: "Avec permis 50cc", filter: s => s.engine_cc > 25 && s.engine_cc <= 50 },
  { value: "125cc", ro: "125cc", en: "125cc", fr: "125cc", filter: s => s.engine_cc > 50 && s.engine_cc <= 125 },
  { value: "mai_mare", ro: "Mai mare", en: "Bigger", fr: "Plus gros", filter: s => s.engine_cc > 125 },
];

function PromoBadge({ badge, text, color }) {
  if (!badge && !text) return null;
  const colors = {
    red: "bg-emerald-600",
    green: "bg-emerald-600",
    orange: "bg-orange-500",
    blue: "bg-blue-600",
  };
  return (
    <div className={`absolute top-3 left-3 z-10 ${colors[color] || colors.red} text-white px-3 py-1.5 rounded-lg shadow-lg font-bold text-sm tracking-wide flex items-center gap-1.5`}>
      <Tag className="w-3.5 h-3.5" />
      {text || badge}
    </div>
  );
}

function ScooterCard({ scooter, t, lang, onOrder, eurRon, onReserve, onView }) {
  const photo = scooter.photos?.[0];
  const whatsappMsg = encodeURIComponent(`Salut! Ma intereseaza: ${scooter.brand} ${scooter.model} ${scooter.year} - ${scooter.selling_price} EUR`);
  const ronPrice = eurRon ? Math.round(scooter.selling_price * eurRon) : null;
  const isReserved = scooter.status === "reserved";
  const isSold = scooter.status === "sold";
  const photoCount = scooter.photos?.length || 0;
  const isNew = scooter.created_at && (Date.now() - new Date(scooter.created_at).getTime()) < 7 * 24 * 60 * 60 * 1000;
  const hasDiscount = scooter.discount_percent > 0;
  // Auto-detect: verified if mechanical check has real scores (not all 0)
  const mc = scooter.mechanical_check;
  const hasScores = mc && Object.keys(mc).some(k => typeof mc[k] === 'number' && mc[k] > 0 && !['scor_general'].includes(k));
  const avgScore = mc ? calculateAverageScore(mc) : null;
  const isVerified = hasScores && avgScore && avgScore > 0;
  return (
    <Card data-testid={`store-scooter-${scooter.scooter_id}`} className={`group overflow-hidden rounded-lg border border-[#262636] hover:border-[#FF0033]/60 hover:shadow-[0_0_30px_#FF003326] transition-[border-color,box-shadow] duration-300 bg-[#13131A] ${isSold ? "opacity-75" : ""}`}>
      <div className="relative aspect-[4/3] overflow-hidden bg-black/40 cursor-pointer" onClick={() => onView(scooter, "scooter")}>
        <PromoBadge badge={scooter.promo_badge} text={scooter.promo_text} color={scooter.promo_color} />
        {photo ? (
          <img src={photo} alt={`${scooter.brand} ${scooter.model}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-600"><Package className="w-16 h-16" /></div>
        )}
        {/* Price tag */}
        <div className={`absolute bottom-3 right-3 ${hasDiscount ? 'bg-[#FF0033]' : 'bg-black/80 border border-[#262636]'} text-white px-4 py-2 rounded-sm font-['Russo_One','Exo_2'] [font-synthesis:none] font-bold text-lg shadow-lg`}>
          {scooter.old_price > 0 && (
            <span className="block text-xs font-normal line-through opacity-70">{scooter.old_price} EUR</span>
          )}
          {scooter.selling_price} EUR
          {ronPrice && <span className="block text-xs font-normal opacity-80">~{ronPrice.toLocaleString()} RON</span>}
        </div>
        {/* Discount badge top-right */}
        {hasDiscount && (
          <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-1">
            <div className="bg-black text-[#FFD700] px-2 py-0.5 rounded-sm text-[9px] font-black tracking-wider border border-[#FF0033]/40">{isBlackFriday() ? "BLACK FRIDAY" : "PROMO"}</div>
            <div className="bg-red-600 text-white px-2 py-1 rounded-lg text-sm font-bold shadow-lg">
              -{scooter.discount_percent}%
            </div>
          </div>
        )}
        {/* NEW badge top-left */}
        {isNew && !isSold && (
          <div className="absolute top-3 left-3 bg-yellow-500 text-black px-3 py-1 rounded-lg text-xs font-bold shadow-lg z-10">
            NOU
          </div>
        )}
        {/* SOLD badge */}
        {isSold && (
          <div className="absolute top-3 left-3 bg-red-600 text-white px-3 py-1.5 rounded text-xs font-bold uppercase">Vandut</div>
        )}
        {isReserved && !isSold && (
          <div className="absolute top-3 right-3 bg-yellow-500 text-black px-2 py-1 rounded text-xs font-bold">{t.reserved}</div>
        )}
        {photoCount > 1 && (
          <div className="absolute top-3 left-3 bg-black/60 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
            <Camera className="w-3 h-3" /> {photoCount}
          </div>
        )}
        {scooter.view_count > 0 && (
          <div className="absolute bottom-3 left-3 bg-black/60 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
            <Eye className="w-3 h-3" /> {scooter.view_count}
          </div>
        )}
      </div>
      <CardContent className="p-4">
        <h3 className={`${DISPLAY} font-extrabold text-2xl text-white mb-1 cursor-pointer hover:text-[#FF0033] transition-colors`} onClick={() => onView(scooter, "scooter")}>{scooter.brand} {scooter.model}</h3>
        <div className="flex flex-wrap gap-2 text-sm text-gray-400 mb-2">
          <span>{scooter.year}</span>
          <span>|</span>
          <span>{scooter.engine_cc} {t.cc}</span>
          <span>|</span>
          <span>{scooter.mileage?.toLocaleString()} {t.km}</span>
        </div>
        {/* Verification badge - auto based on mechanical scores */}
        <div className="mb-2">
          {isVerified ? (
            <span className="inline-flex items-center gap-1 text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">
              <CheckCircle className="w-3 h-3" /> Verificat - Nota: {avgScore}/5
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full">
              <Info className="w-3 h-3" /> Neverificat de noi
            </span>
          )}
        </div>
        {hasDiscount && !isSold && isBlackFriday() && (
          <div data-testid={`stock-limited-${scooter.scooter_id}`} className="text-[11px] font-bold text-[#FFD700] mb-2 flex items-center gap-1"><Clock className="w-3 h-3" />Stoc foarte limitat · economisești {Math.max(0, (scooter.old_price || 0) - (scooter.selling_price || 0))} EUR</div>
        )}
        <div className="flex gap-2">
          {isSold ? (
            <Button disabled className="flex-1 bg-red-500/20 text-red-400 text-sm cursor-not-allowed">
              Vandut recent
            </Button>
          ) : !isReserved ? (
            <>
              <Button data-testid={`order-btn-${scooter.scooter_id}`} onClick={() => onOrder(scooter)} className="flex-1 bg-[#FF0033] hover:bg-[#e0002d] text-white text-sm font-bold rounded-sm">
                <ShoppingCart className="w-4 h-4 mr-1.5" />{t.btn_order}
              </Button>
              <Button data-testid={`reserve-btn-${scooter.scooter_id}`} onClick={() => onReserve(scooter)} variant="outline" className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10 text-sm px-3">
                <Clock className="w-4 h-4 mr-1" />{t.btn_reserve || "Rezerva"}
              </Button>
            </>
          ) : (
            <Button disabled className="flex-1 bg-yellow-500/20 text-yellow-400 text-sm cursor-not-allowed">
              <Clock className="w-4 h-4 mr-1.5" />{t.reserved}
            </Button>
          )}
          <a href={`https://wa.me/${WHATSAPP}?text=${whatsappMsg}`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="border-green-500/50 text-green-400 hover:bg-green-500/10 text-sm px-3">
              <MessageCircle className="w-4 h-4" />
            </Button>
          </a>
        </div>
      </CardContent>
    </Card>
  );
}

function PartCard({ part, t, lang, onOrder, onRequest, eurRon, onView }) {
  const photo = part.photos?.[0];
  const ronPrice = eurRon ? Math.round(part.price * eurRon) : null;
  const photoCount = part.photos?.length || 0;
  return (
    <Card data-testid={`store-part-${part.part_id}`} className="group overflow-hidden border border-white/10 hover:border-emerald-500/40 hover:shadow-xl hover:shadow-emerald-500/5 transition-all duration-300 bg-[#111113]">
      <div className="relative aspect-square overflow-hidden bg-black/40 cursor-pointer" onClick={() => onView(part, "part")}>
        {photo ? (
          <img src={photo} alt={part.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-600"><Wrench className="w-12 h-12" /></div>
        )}
        <div className="absolute bottom-2 right-2 bg-emerald-600 text-white px-3 py-1.5 rounded-lg font-bold">
          {part.price} EUR
          {ronPrice && <span className="block text-[10px] font-normal opacity-80">~{ronPrice} RON</span>}
        </div>
        {part.quantity > 0 && (
          <div className="absolute top-2 right-2 bg-emerald-600 text-white text-xs px-2 py-1 rounded">{t.in_stock}: {part.quantity}</div>
        )}
        {photoCount > 1 && (
          <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
            <Camera className="w-3 h-3" /> {photoCount}
          </div>
        )}
        {part.view_count > 0 && (
          <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-1.5 py-1 rounded flex items-center gap-1">
            <Eye className="w-3 h-3" /> {part.view_count}
          </div>
        )}
      </div>
      <CardContent className="p-3">
        <h3 className="font-semibold text-sm text-white mb-1 truncate cursor-pointer hover:text-emerald-400 transition-colors" onClick={() => onView(part, "part")}>{part.name}</h3>
        {part.compatible_brands?.length > 0 && (
          <p className="text-xs text-gray-500 mb-2 truncate">{part.compatible_brands.join(", ")}</p>
        )}
        <Button data-testid={`order-part-btn-${part.part_id}`} onClick={() => onOrder(part, "part")} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8">
          <ShoppingCart className="w-3 h-3 mr-1" />{t.btn_order}
        </Button>
      </CardContent>
    </Card>
  );
}

function CasseCard({ scooter, t }) {
  const [showForm, setShowForm] = useState(false);
  const [request, setRequest] = useState({ name: "", phone: "", description: "" });
  const [submitted, setSubmitted] = useState(false);
  const photos = scooter.photos || [];
  const [photoIdx, setPhotoIdx] = useState(0);

  const whatsappMsg = encodeURIComponent(`Salut! Caut piese de la: ${scooter.brand} ${scooter.model} ${scooter.year} (${scooter.engine_cc}cc). Piesa dorita: `);

  const handleSubmit = async () => {
    if (!request.name || !request.phone || !request.description) {
      toast.error("Completati toate campurile");
      return;
    }
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL || ''}/api/public/part-requests`, {
        scooter_brand: scooter.brand,
        scooter_model: scooter.model,
        part_description: request.description,
        first_name: request.name,
        phone: request.phone,
      });
      setSubmitted(true);
      toast.success("Cererea a fost trimisa! Va contactam in curand.");
    } catch {
      toast.error("Eroare la trimitere");
    }
  };

  return (
    <Card className="overflow-hidden bg-[#111113] border-white/10 hover:border-orange-500/30 transition-all" data-testid={`casse-${scooter.scooter_id}`}>
      {/* Photo Gallery */}
      <div className="relative aspect-[4/3] bg-black/40 overflow-hidden">
        {photos.length > 0 ? (
          <img src={photos[photoIdx]} alt={`${scooter.brand} ${scooter.model}`} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-600"><Package className="w-16 h-16" /></div>
        )}
        {photos.length > 1 && (
          <>
            <button onClick={() => setPhotoIdx(i => (i - 1 + photos.length) % photos.length)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 text-white w-8 h-8 rounded-full flex items-center justify-center">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => setPhotoIdx(i => (i + 1) % photos.length)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 text-white w-8 h-8 rounded-full flex items-center justify-center">
              <ChevronRight className="w-4 h-4" />
            </button>
            <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
              {photoIdx + 1}/{photos.length}
            </div>
          </>
        )}
        <div className="absolute top-2 left-2 bg-orange-500 text-black text-xs font-bold px-2 py-1 rounded">DEZMEMBRARE</div>
      </div>

      <CardContent className="p-4 space-y-3">
        <div>
          <h3 className="font-bold text-lg text-white">{scooter.brand} {scooter.model}</h3>
          <p className="text-sm text-gray-400">{scooter.year} | {scooter.engine_cc}cc | {scooter.color}</p>
          {scooter.notes && <p className="text-xs text-gray-500 mt-1">{scooter.notes}</p>}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button onClick={() => setShowForm(!showForm)} className={`flex-1 text-sm ${showForm ? "bg-orange-500 hover:bg-orange-600" : "bg-orange-600/20 hover:bg-orange-600/30 text-orange-400 border border-orange-500/30"}`} data-testid={`casse-request-${scooter.scooter_id}`}>
            <Wrench className="w-4 h-4 mr-1.5" /> Cere piese
          </Button>
          <a href={`https://wa.me/${WHATSAPP}?text=${whatsappMsg}`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="border-green-500/50 text-green-400 hover:bg-green-500/10 text-sm px-3">
              <MessageCircle className="w-4 h-4" />
            </Button>
          </a>
          <a href={`tel:${PHONE}`}>
            <Button variant="outline" className="border-white/20 text-gray-300 hover:bg-white/5 text-sm px-3">
              <Phone className="w-4 h-4" />
            </Button>
          </a>
        </div>

        {/* Request Form */}
        {showForm && !submitted && (
          <div className="space-y-2 bg-white/5 rounded-lg p-3 border border-orange-500/20 animate-in fade-in duration-200">
            <p className="text-xs text-gray-400">Descrieti piesa pe care o cautati de la acest scuter:</p>
            <Input placeholder="Numele tau" value={request.name} onChange={e => setRequest(r => ({ ...r, name: e.target.value }))} className="h-8 text-sm" />
            <Input placeholder="Telefon" value={request.phone} onChange={e => setRequest(r => ({ ...r, phone: e.target.value }))} className="h-8 text-sm" />
            <Textarea placeholder="Ce piesa cautati? (ex: carena fata, oglinzi, far stanga...)" value={request.description} onChange={e => setRequest(r => ({ ...r, description: e.target.value }))} rows={2} className="text-sm" />
            <Button onClick={handleSubmit} className="w-full bg-orange-500 hover:bg-orange-600 text-sm h-8">Trimite cererea</Button>
          </div>
        )}
        {submitted && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-center">
            <CheckCircle className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
            <p className="text-sm text-emerald-400 font-semibold">Cerere trimisa!</p>
            <p className="text-xs text-gray-400">Va contactam in curand cu pretul si disponibilitatea.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ProductLinkShare({ item, isScooter }) {
  const base = API || window.location.origin;
  const link = isScooter ? `${base}/api/share/scooter/${encodeURIComponent(item.scooter_id)}` : `${base}/api/share/part/${encodeURIComponent(item.part_id)}`;
  const title = isScooter ? `${item.brand} ${item.model} ${item.year || ""} - ${item.selling_price} EUR` : `${item.name} - ${item.price} EUR`;
  const waText = encodeURIComponent(`${title}\nVezi pe Scutersh: ${link}`);
  const copy = () => { navigator.clipboard.writeText(link); toast.success("Link copiat!"); };
  const nativeShare = () => navigator.share({ title, url: link }).catch(() => {});
  const btn = "flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border transition-colors";
  return (
    <div className="bg-[#FF0033]/5 border border-[#FF0033]/30 rounded-lg p-4" data-testid="product-link-share">
      <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2 uppercase tracking-wide"><Share2 className="w-4 h-4 text-[#FF0033]" /> Trimite acest produs</h3>
      <div className="flex flex-wrap gap-2">
        <a data-testid="share-whatsapp" href={`https://wa.me/?text=${waText}`} target="_blank" rel="noopener noreferrer" className={`${btn} bg-green-500/10 text-green-400 border-green-500/30 hover:bg-green-500/20`}><MessageCircle className="w-4 h-4" /> WhatsApp</a>
        <a data-testid="share-facebook" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`} target="_blank" rel="noopener noreferrer" className={`${btn} bg-blue-600/10 text-blue-300 border-blue-600/30 hover:bg-blue-600/20`}><Share2 className="w-4 h-4" /> Facebook</a>
        <button data-testid="share-copy-link" onClick={copy} className={`${btn} bg-white/5 text-slate-200 border-white/15 hover:bg-white/10`}><Copy className="w-4 h-4" /> Copiază link</button>
        {typeof navigator !== "undefined" && navigator.share && (
          <button data-testid="share-native" onClick={nativeShare} className={`${btn} bg-white/5 text-slate-200 border-white/15 hover:bg-white/10`}><ArrowRight className="w-4 h-4" /> Altele</button>
        )}
      </div>
    </div>
  );
}

function DetailModalContent({ item, eurRon, detailPhotoIndex, setDetailPhotoIndex, t, onOrder, onReserve, onClose }) {
  const photos = item.photos || [];
  const videos = item.videos || [];
  const isScooter = item._type === "scooter";
  const price = isScooter ? item.selling_price : item.price;
  const ronPrice = eurRon ? Math.round(price * eurRon) : null;
  const whatsappMsg = encodeURIComponent(
    isScooter
      ? `Salut! Ma intereseaza: ${item.brand} ${item.model} ${item.year} - ${price} EUR`
      : `Salut! Ma intereseaza piesa: ${item.name} - ${price} EUR`
  );
  const mc = item.mechanical_check;
  const tf = item.technical_file;
  const avgScore = mc ? calculateAverageScore(mc) : null;
  const condition = avgScore ? getOverallCondition(avgScore) : null;
  const [fullscreen, setFullscreen] = useState(false);
  const [showVideo, setShowVideo] = useState(null);

  return (
    <>
      {/* Fullscreen photo overlay */}
      {fullscreen && photos[detailPhotoIndex] && (
        <div className="fixed inset-0 z-[100] bg-black flex items-center justify-center" onClick={() => setFullscreen(false)} data-testid="fullscreen-overlay">
          <img src={photos[detailPhotoIndex]} alt="" className="max-w-full max-h-full object-contain" />
          <button onClick={() => setFullscreen(false)} className="absolute top-4 right-4 bg-white/20 text-white w-10 h-10 rounded-full flex items-center justify-center">
            <X className="w-6 h-6" />
          </button>
          {photos.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); setDetailPhotoIndex(i => (i - 1 + photos.length) % photos.length); }} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 text-white w-12 h-12 rounded-full flex items-center justify-center">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); setDetailPhotoIndex(i => (i + 1) % photos.length); }} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 text-white w-12 h-12 rounded-full flex items-center justify-center">
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          <div className="absolute bottom-4 text-white text-sm font-mono">{detailPhotoIndex + 1} / {photos.length}</div>
        </div>
      )}

      {/* Video overlay */}
      {showVideo && (
        <div className="fixed inset-0 z-[100] bg-black flex items-center justify-center" onClick={() => setShowVideo(null)}>
          <video src={showVideo} controls autoPlay className="max-w-full max-h-full" onClick={e => e.stopPropagation()} />
          <button onClick={() => setShowVideo(null)} className="absolute top-4 right-4 bg-white/20 text-white w-10 h-10 rounded-full flex items-center justify-center">
            <X className="w-6 h-6" />
          </button>
        </div>
      )}

      {/* Photo Gallery - click for fullscreen */}
      <div className="relative aspect-[16/10] bg-black cursor-pointer" data-testid="detail-gallery" onClick={() => photos.length > 0 && setFullscreen(true)}>
        {photos.length > 0 ? (
          <img src={photos[detailPhotoIndex]} alt="Product" className="w-full h-full object-contain" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-600">
            {isScooter ? <Package className="w-20 h-20" /> : <Wrench className="w-20 h-20" />}
          </div>
        )}
        {photos.length > 1 && (
          <>
            <button onClick={(e) => { e.stopPropagation(); setDetailPhotoIndex(i => (i - 1 + photos.length) % photos.length); }} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white w-10 h-10 rounded-full flex items-center justify-center transition-colors" data-testid="detail-photo-prev">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); setDetailPhotoIndex(i => (i + 1) % photos.length); }} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white w-10 h-10 rounded-full flex items-center justify-center transition-colors" data-testid="detail-photo-next">
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute bottom-3 right-3 bg-black/60 text-white text-sm px-3 py-1 rounded-full font-mono">
              {detailPhotoIndex + 1} / {photos.length}
            </div>
          </>
        )}
        {/* Thumbnails */}
        {photos.length > 1 && (
          <div className="absolute bottom-3 left-3 right-3 flex gap-2 overflow-x-auto py-1">
            {photos.map((p, i) => (
              <img key={`dt-${p.slice(-10)}`} src={p} alt={`Photo ${i + 1}`} onClick={(e) => { e.stopPropagation(); setDetailPhotoIndex(i); }}
                className={`w-14 h-14 rounded object-cover cursor-pointer border-2 flex-shrink-0 transition-all ${i === detailPhotoIndex ? "border-emerald-400 opacity-100" : "border-transparent opacity-50 hover:opacity-80"}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Videos Section */}
      {videos.length > 0 && (
        <div className="px-5 pt-3">
          <p className="text-xs font-semibold text-muted-foreground mb-2">Video ({videos.length})</p>
          <div className="flex gap-2 overflow-x-auto">
            {videos.map((v, i) => (
              <button key={`vid-${v.slice(-10)}`} onClick={() => setShowVideo(v)} className="flex-shrink-0 w-24 h-16 bg-black rounded-lg border border-border flex items-center justify-center hover:border-primary transition-colors">
                <Play className="w-8 h-8 text-primary" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-5 space-y-5">
        {/* Title & Price */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold" data-testid="detail-title">
              {isScooter ? `${item.brand} ${item.model}` : item.name}
            </h2>
            {isScooter && (
              <p className="text-gray-400 text-sm mt-0.5">{item.year} | {item.engine_cc} cc | {item.mileage?.toLocaleString()} km</p>
            )}
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-2xl font-bold text-emerald-400">{price} EUR</p>
            {ronPrice && <p className="text-sm text-gray-500">~{ronPrice.toLocaleString()} RON</p>}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button onClick={onOrder} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="detail-order-btn">
            <ShoppingCart className="w-4 h-4 mr-2" /> {t.btn_order}
          </Button>
          {isScooter && item.status !== "reserved" && (
            <Button onClick={onReserve} variant="outline" className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10" data-testid="detail-reserve-btn">
              <Clock className="w-4 h-4 mr-2" /> {t.btn_reserve || "Rezerva"}
            </Button>
          )}
          <a href={`https://wa.me/${WHATSAPP}?text=${whatsappMsg}`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="border-green-500/50 text-green-400 hover:bg-green-500/10">
              <MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp
            </Button>
          </a>
        </div>

        {/* === FIȘĂ TEHNICĂ === */}
        {isScooter && (
          <div className="bg-white/5 rounded-lg p-4" data-testid="detail-fisa-tehnica">
            <h3 className="text-sm font-semibold text-emerald-400 mb-3 flex items-center gap-2 uppercase tracking-wide">
              <Settings className="w-4 h-4" /> Fisa Tehnica
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Marca</span><span className="text-white font-mono">{item.brand}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Model</span><span className="text-white font-mono">{item.model}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">An</span><span className="text-white font-mono">{item.year}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Motor</span><span className="text-white font-mono">{item.engine_cc} cc</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Km</span><span className="text-white font-mono">{item.mileage?.toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Culoare</span><span className="text-white font-mono">{item.color || "—"}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">VIN</span><span className="text-white font-mono text-xs">{item.vin || "—"}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Status</span><span className="text-white font-mono">{item.status === "in_stock" ? "Disponibil" : item.status === "reserved" ? "Rezervat" : item.status}</span></div>
              {tf && tf.engine_type && <div className="flex justify-between"><span className="text-gray-500">Tip motor</span><span className="text-white font-mono">{tf.engine_type}</span></div>}
              {tf && tf.transmission && <div className="flex justify-between"><span className="text-gray-500">Transmisie</span><span className="text-white font-mono">{tf.transmission}</span></div>}
              {tf && tf.cooling_system && <div className="flex justify-between"><span className="text-gray-500">Racire</span><span className="text-white font-mono">{tf.cooling_system}</span></div>}
              {tf && tf.fuel_type && <div className="flex justify-between"><span className="text-gray-500">Combustibil</span><span className="text-white font-mono">{tf.fuel_type}</span></div>}
              {tf && tf.max_speed && <div className="flex justify-between"><span className="text-gray-500">Viteza max</span><span className="text-white font-mono">{tf.max_speed}</span></div>}
              {tf && tf.consumption && <div className="flex justify-between"><span className="text-gray-500">Consum</span><span className="text-white font-mono">{tf.consumption}</span></div>}
            </div>
          </div>
        )}

        {/* === STARE TEHNICĂ (Fiche Publique) === */}
        {isScooter && mc && (
          <div className="bg-white/5 rounded-lg overflow-hidden" data-testid="detail-stare-tehnica">
            {/* Trust Banner */}
            <div className="bg-gradient-to-r from-emerald-600/30 to-emerald-400/10 px-4 py-3 border-b border-emerald-500/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-emerald-400 uppercase tracking-wide">Verificat Mecanic Scutersh</h3>
                    <p className="text-[10px] text-gray-400">Fisa de control completa - {Object.keys(mc).filter(k => !k.includes('_comment') && !k.includes('_action') && !k.includes('_photos') && !k.includes('service_info') && !k.includes('observations') && !k.includes('recomandari') && !k.includes('aprobat') && !k.includes('scor') && !k.includes('acte') && !k.includes('tip_acte') && !k.includes('tara')).length} puncte inspectate</p>
                  </div>
                </div>
                {avgScore && (
                  <div className="text-center">
                    <div className={`font-mono font-bold text-2xl ${condition?.color || "text-white"}`}>{avgScore}<span className="text-sm text-gray-400">/5</span></div>
                    <span className="text-[10px] text-gray-400">{condition?.label}</span>
                  </div>
                )}
              </div>
              {mc.aprobat_utilizare !== false && (
                <div className="mt-2 flex items-center gap-1 text-emerald-400 text-xs font-medium">
                  <CheckCircle className="w-3 h-3" /> Aprobat pentru utilizare
                </div>
              )}
            </div>

            {/* Mechanic Photos Grid */}
            {(() => {
              const mechPhotos = Object.entries(mc).filter(([k, v]) => k.endsWith('_photos') && Array.isArray(v) && v.length > 0);
              if (mechPhotos.length === 0) return null;
              const allPhotos = mechPhotos.flatMap(([, v]) => v);
              return (
                <div className="px-4 py-3 border-b border-white/5">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-2">Fotografii verificare mecanic</p>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {allPhotos.slice(0, 8).map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noreferrer" className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border border-white/10 hover:border-emerald-500/50 transition-colors">
                        <img src={url} alt="" className="w-full h-full object-cover" />
                      </a>
                    ))}
                    {allPhotos.length > 8 && <span className="flex-shrink-0 w-16 h-16 rounded-lg bg-white/5 flex items-center justify-center text-xs text-gray-400">+{allPhotos.length - 8}</span>}
                  </div>
                </div>
              );
            })()}

            {/* Mechanic Recommendations */}
            {mc.recomandari_mecanic && (
              <div className="px-4 py-3 border-b border-white/5">
                <p className="text-[10px] text-emerald-400 uppercase tracking-wide mb-1">Recomandari mecanic</p>
                <p className="text-sm text-gray-300">{mc.recomandari_mecanic}</p>
              </div>
            )}

            {/* Full Checklist */}
            <div className="p-4">
              <MechanicalChecklistDisplay values={mc} />
            </div>
          </div>
        )}

        {/* === REPARATII & GARANTIE === */}
        {isScooter && item.repairs_done && (
          <div className="bg-white/5 rounded-lg p-4" data-testid="detail-repairs">
            <h3 className="text-sm font-semibold text-gray-300 mb-2">Reparatii efectuate</h3>
            <p className="text-sm text-gray-400">{item.repairs_done}</p>
          </div>
        )}
        {isScooter && item.warranty_info && (
          <div className="bg-emerald-600/10 border border-emerald-500/30 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-emerald-400 mb-2">Garantie</h3>
            <p className="text-sm text-gray-400">{item.warranty_info}</p>
          </div>
        )}

        {/* === Part Details === */}
        {!isScooter && (
          <div className="bg-white/5 rounded-lg p-4" data-testid="detail-specs">
            <h3 className="text-sm font-semibold text-emerald-400 mb-3 flex items-center gap-2 uppercase tracking-wide">
              <Info className="w-4 h-4" /> Detalii piesa
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Categorie</span><span className="text-white">{item.category || "—"}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Stare</span><span className="text-white">{item.condition === "bon" ? "Buna" : item.condition === "neuf" ? "Noua" : item.condition || "—"}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Cantitate</span><span className="text-white">{item.quantity}</span></div>
              {item.compatible_brands?.length > 0 && (
                <div className="col-span-2"><span className="text-gray-500">Compatibil:</span> <span className="text-white ml-1">{item.compatible_brands.join(", ")} {item.compatible_models?.length > 0 ? `- ${item.compatible_models.join(", ")}` : ""}</span></div>
              )}
            </div>
          </div>
        )}

        {/* === DE CE SĂ CUMPERI DE LA NOI? === */}
        {isScooter && (
          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4" data-testid="detail-why-buy">
            <h3 className="text-sm font-semibold text-emerald-400 mb-3 flex items-center gap-2 uppercase tracking-wide">
              <Shield className="w-4 h-4" /> De ce sa cumperi de la noi?
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <span className="text-emerald-400 font-bold text-sm flex-shrink-0">[+]</span>
                <p className="text-sm text-gray-300"><strong className="text-white">Transparenta totala</strong> - Fiecare scuter are un dosar digital complet cu istoricul reparatiilor si verificarile tehnice</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-emerald-400 font-bold text-sm flex-shrink-0">[+]</span>
                <p className="text-sm text-gray-300"><strong className="text-white">Ne ocupam de toate reparatiile</strong> - Scuterele sunt verificate si pregatite pentru utilizare</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-emerald-400 font-bold text-sm flex-shrink-0">[+]</span>
                <p className="text-sm text-gray-300"><strong className="text-white">Calitate garantata</strong> - Selectam doar scutere in stare buna cu consultanta post-vanzare</p>
              </div>
            </div>
          </div>
        )}

        <ProductLinkShare item={item} isScooter={isScooter} />

        {/* === SHARE BUTTONS === */}
        {isScooter && (
          <div className="bg-white/5 rounded-lg p-4" data-testid="detail-share">
            <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2 uppercase tracking-wide">
              <Share2 className="w-4 h-4" /> Partajeaza publicatia
            </h3>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => {
                const boutiqueUrl = `${window.location.origin}/boutique`;
                const noteText = mc && avgScore ? `Nota tehnica: ${avgScore}/5 - ${condition?.label || ''}\n` : '';
                const text = `${item.brand} ${item.model} ${item.year}\n${item.engine_cc}cc | ${item.mileage?.toLocaleString()} km | ${item.color}\nPret: ${price} EUR\n${noteText}\nVerificat tehnic. Garantie inclusa.\n\nVrei alte scutere sau piese? Pe magazinul nostru gasesti scutere, piese de schimb si scutere pentru dezmembrare!\nNu gasesti ce cauti? Cere-ne si iti oferim cel mai bun pret!\nLivram in toata Romania.\n\n${boutiqueUrl}\n\noffice@scutersh.ro | 0792250058\n\n#scutersh #scuter #${item.brand?.toLowerCase()?.replace(/\s/g,'')} #scutersecondhand #piese #romania`;
                navigator.clipboard.writeText(text);
                toast.success("Text Instagram copiat!");
              }} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-md bg-pink-500/10 text-pink-400 border border-pink-500/20 hover:bg-pink-500/20 transition-colors">
                <Instagram className="w-4 h-4" /> Instagram
              </button>
              <button onClick={() => {
                const boutiqueUrl = `${window.location.origin}/boutique`;
                const noteText = mc && avgScore ? `- Nota tehnica: ${avgScore}/5 - ${condition?.label || ''}\n` : '';
                const text = `${item.brand} ${item.model} ${item.year} - ${price} EUR\n\nCaracteristici:\n- Motor: ${item.engine_cc}cc\n- Kilometraj: ${item.mileage?.toLocaleString()} km\n- Culoare: ${item.color}\n- Stare: Verificat tehnic\n${noteText}${item.repairs_done ? `- Reparatii: ${item.repairs_done}\n` : ''}${item.warranty_info ? `- Garantie: ${item.warranty_info}\n` : ''}\nScuter verificat si pregatit pentru utilizare.\n\nMagazin online cu scutere, piese de schimb si dezmembrari:\n${boutiqueUrl}\n\nNu gasesti ce cauti? Contacteaza-ne si iti oferim cel mai bun pret!\nLivram in toata Romania.\n\nContact: office@scutersh.ro | 0792250058 | WhatsApp: +40792250058`;
                navigator.clipboard.writeText(text);
                toast.success("Text OLX copiat!");
              }} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-md bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 transition-colors">
                <Copy className="w-4 h-4" /> OLX
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default function BoutiquePage() {
  const [lang, setLang] = useState("ro");
  const t = TRANSLATIONS[lang];

  useSEO({
    title: "Scutersh - Scutere & Piese Second Hand | Magazin Online Romania",
    description: "Magazin online de scutere si piese second hand verificate. Preturi de la 300 EUR. Livrare in toata Romania. Garantie si transparenta totala.",
    url: window.location.href,
    image: LOGO_URL,
  });

  const [activeTab, setActiveTab] = usePersistedState("boutique:tab", "scooters");
  const [catDiscounts, setCatDiscounts] = useState({});
  const bf = isBlackFriday();
  const [scooters, setScooters] = useState([]);
  const [parts, setParts] = useState([]);
  const [casse, setCasse] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = usePersistedState("boutique:search", "");
  const [filterBrand, setFilterBrand] = useState("all");
  const [filterPriceRange, setFilterPriceRange] = useState("all");
  const [filterType, setFilterType] = usePersistedState("boutique:type", "all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [sortBy, setSortBy] = useState("auto");

  // Modals
  const [orderModal, setOrderModal] = useState(null);
  const [requestModal, setRequestModal] = useState(false);
  const [orderForm, setOrderForm] = useState({ first_name: "", last_name: "", phone: "", email: "", delivery_address: "", city: "", zone_id: "", notes: "" });
  const [requestForm, setRequestForm] = useState({ first_name: "", last_name: "", phone: "", email: "", scooter_brand: "", scooter_model: "", scooter_year: "", part_description: "", notes: "" });
  const [eurRon, setEurRon] = useState(null);
  const [zones, setZones] = useState([]);
  const [lastOrderResult, setLastOrderResult] = useState(null);
  const [reserveModal, setReserveModal] = useState(null);
  const [reserveForm, setReserveForm] = useState({ first_name: "", last_name: "", phone: "", email: "", notes: "" });
  const [lastReserveResult, setLastReserveResult] = useState(null);
  const [casseSearch, setCasseSearch] = useState("");

  const filteredCasse = useMemo(() => {
    if (!casseSearch) return casse;
    const q = casseSearch.toLowerCase();
    return casse.filter(s => `${s.brand} ${s.model} ${s.color} ${s.engine_cc}`.toLowerCase().includes(q));
  }, [casse, casseSearch]);

  const [detailItem, setDetailItem] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [recentIds, setRecentIds] = useState(getRecentIds);
  const productParam = (item, type) => type === "scooter" ? `produs=${encodeURIComponent(item.scooter_id)}` : `piesa=${encodeURIComponent(item.part_id)}`;
  const openDetail = (item, type, deep = false) => {
    setDetailItem({ ...item, _type: type });
    setDetailPhotoIndex(0);
    if (type === "scooter") setRecentIds(addRecentId(item.scooter_id));
    if (deep) navigate(`${location.pathname}?${productParam(item, type)}`, { replace: true, state: { produs: true, deep: true } });
    else if (!window.history.state?.usr?.produs) navigate(`${location.pathname}?${productParam(item, type)}`, { state: { produs: true } });
  };
  const closeDetail = () => {
    const st = window.history.state?.usr;
    if (st?.deep) { setDetailItem(null); navigate(location.pathname, { replace: true }); }
    else if (st?.produs) navigate(-1);
    else setDetailItem(null);
  };
  useEffect(() => { if (!location.state?.produs) setDetailItem(null); }, [location.state]);

  const [detailPhotoIndex, setDetailPhotoIndex] = useState(0);
  useScrollRestore("boutique", !loading);
  const [linkCopied, setLinkCopied] = useState(false);

  const copyBoutiqueLink = () => {
    const url = `${API}/api/share/boutique`;
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(true);
      toast.success("Lien copié! (optimisé Facebook)");
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [scRes, ptRes, csRes, brRes, rateRes, zonesRes, discRes] = await Promise.all([
          fetch(`${API}/api/public/store/scooters`),
          fetch(`${API}/api/public/store/parts`),
          fetch(`${API}/api/public/store/casse`),
          fetch(`${API}/api/public/store/brands`),
          fetch(`${API}/api/public/exchange-rate`),
          fetch(`${API}/api/public/delivery-zones`),
          fetch(`${API}/api/public/store/category-discounts`),
        ]);
        setScooters(await scRes.json());
        setParts(await ptRes.json());
        setCasse(await csRes.json());
        setBrands(await brRes.json());
        const rateData = await rateRes.json();
        setEurRon(rateData.eur_ron);
        setZones(await zonesRes.json());
        if (discRes.ok) setCatDiscounts(await discRes.json());
      } catch (e) {
        console.error("Failed to load store data", e);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const deepLinkDone = useRef(false);
  useEffect(() => {
    if (loading || deepLinkDone.current) return;
    deepLinkDone.current = true;
    const params = new URLSearchParams(location.search);
    const sid = params.get("produs");
    const pid = params.get("piesa");
    const sc = sid && scooters.find(x => x.scooter_id === sid);
    const pt = pid && parts.find(x => x.part_id === pid);
    if (sc) { setActiveTab("scooters"); openDetail(sc, "scooter", true); }
    else if (pt) { setActiveTab("parts"); openDetail(pt, "part", true); }
    else if (sid || pid) { navigate(location.pathname, { replace: true }); toast.error("Produsul nu mai este disponibil"); }
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredScooters = useMemo(() => {
    let list = scooters;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(s => `${s.brand} ${s.model} ${s.color}`.toLowerCase().includes(q));
    }
    if (filterBrand !== "all") list = list.filter(s => s.brand.trim() === filterBrand.trim());
    if (filterType !== "all") {
      const typeObj = SCOOTER_TYPES.find(t => t.value === filterType);
      if (typeObj && typeObj.filter) list = list.filter(typeObj.filter);
    }
    if (filterPriceRange !== "all") {
      const [min, max] = filterPriceRange.split("-").map(Number);
      list = list.filter(s => s.selling_price >= min && (!max || s.selling_price <= max));
    }
    // Sorting
    if (sortBy === "price_asc") list = [...list].sort((a, b) => a.selling_price - b.selling_price);
    else if (sortBy === "price_desc") list = [...list].sort((a, b) => b.selling_price - a.selling_price);
    else if (sortBy === "year_desc") list = [...list].sort((a, b) => b.year - a.year);
    else if (sortBy === "year_asc") list = [...list].sort((a, b) => a.year - b.year);
    else if (sortBy === "note") list = [...list].sort((a, b) => (b.mechanical_check?.scor_general || 0) - (a.mechanical_check?.scor_general || 0));
    return list;
  }, [scooters, search, filterBrand, filterType, filterPriceRange, sortBy]);

  const filteredParts = useMemo(() => {
    let list = parts;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p => `${p.name} ${(p.compatible_brands || []).join(" ")} ${(p.compatible_models || []).join(" ")}`.toLowerCase().includes(q));
    }
    if (filterCategory !== "all") list = list.filter(p => p.category === filterCategory);
    if (filterBrand !== "all") list = list.filter(p => (p.compatible_brands || []).some(b => b.trim() === filterBrand.trim()));
    if (filterPriceRange !== "all") {
      const [min, max] = filterPriceRange.split("-").map(Number);
      list = list.filter(p => p.price >= min && (!max || p.price <= max));
    }
    // Sorting
    if (sortBy === "price_asc") list = [...list].sort((a, b) => a.price - b.price);
    else if (sortBy === "price_desc") list = [...list].sort((a, b) => b.price - a.price);
    return list;
  }, [parts, search, filterCategory, filterBrand, filterPriceRange, sortBy]);

  const handleOrder = (item, type = "scooter") => {
    const name = type === "scooter" ? `${item.brand} ${item.model} ${item.year}` : item.name;
    const id = type === "scooter" ? item.scooter_id : item.part_id;
    setOrderModal({ item_type: type, item_id: id, item_name: name, price: type === "scooter" ? item.selling_price : item.price, item_price: type === "scooter" ? item.selling_price : item.price });
  };

  const submitOrder = async () => {
    if (!orderForm.first_name || !orderForm.last_name || !orderForm.phone) {
      toast.error("Completati numele si telefonul!");
      return;
    }
    try {
      const resp = await fetch(`${API}/api/public/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...orderForm, ...orderModal }),
      });
      if (resp.ok) {
        const result = await resp.json();
        setLastOrderResult(result);
        setOrderModal(null);
        setOrderForm({ first_name: "", last_name: "", phone: "", email: "", delivery_address: "", city: "", zone_id: "", notes: "" });
      }
    } catch (e) {
      toast.error("Eroare la trimitere!");
    }
  };

  const submitPartRequest = async () => {
    if (!requestForm.first_name || !requestForm.phone || !requestForm.part_description) {
      toast.error("Completati numele, telefonul si descrierea piesei!");
      return;
    }
    try {
      const resp = await fetch(`${API}/api/public/part-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestForm),
      });
      if (resp.ok) {
        toast.success(t.request_success);
        setRequestModal(false);
        setRequestForm({ first_name: "", last_name: "", phone: "", email: "", scooter_brand: "", scooter_model: "", scooter_year: "", part_description: "", notes: "" });
      }
    } catch (e) {
      toast.error("Eroare la trimitere!");
    }
  };

  const handleReserve = (scooter) => {
    setReserveModal(scooter);
  };

  const submitReservation = async () => {
    if (!reserveForm.first_name || !reserveForm.last_name || !reserveForm.phone) {
      toast.error("Completati numele si telefonul!");
      return;
    }
    try {
      const resp = await fetch(`${API}/api/public/reservations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...reserveForm, scooter_id: reserveModal.scooter_id }),
      });
      if (resp.ok) {
        const result = await resp.json();
        setLastReserveResult(result);
        setReserveModal(null);
        setReserveForm({ first_name: "", last_name: "", phone: "", email: "", notes: "" });
        // Refresh scooter list to show reserved status
        const scRes = await fetch(`${API}/api/public/store/scooters`);
        setScooters(await scRes.json());
      } else {
        const err = await resp.json();
        toast.error(err.detail || "Eroare la rezervare");
      }
    } catch (e) {
      toast.error("Eroare la trimitere!");
    }
  };

  const resetFilters = () => {
    setSearch("");
    setFilterBrand("all");
    setFilterPriceRange("all");
    setFilterType("all");
    setFilterCategory("all");
    setSortBy("auto");
  };

  const priceRanges = activeTab === "scooters"
    ? [{ v: "0-500", l: "0 - 500 EUR" }, { v: "500-1000", l: "500 - 1000 EUR" }, { v: "1000-2000", l: "1000 - 2000 EUR" }, { v: "2000-99999", l: "2000+ EUR" }]
    : [{ v: "0-20", l: "0 - 20 EUR" }, { v: "20-50", l: "20 - 50 EUR" }, { v: "50-100", l: "50 - 100 EUR" }, { v: "100-99999", l: "100+ EUR" }];

  const currentItems = activeTab === "scooters" ? filteredScooters : filteredParts;
  const maxDiscount = Math.max(0, ...scooters.filter(s => s.status !== "sold").map(s => s.discount_percent || 0));

  return (
    <div data-testid="boutique-page" className="min-h-screen bg-[#0A0A0E] text-white font-['Exo_2']">
      <Toaster position="top-center" />
      <JsonLdOrganization />

      {bf && <UrgencyMarquee />}

      {/* Top Bar */}
      <div className="bg-[#0A0A0E] border-b border-[#262636]">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between text-xs">
          <a href={`tel:${PHONE}`} data-testid="topbar-phone" className="flex items-center gap-1.5 text-slate-300 hover:text-white"><Phone className="w-3.5 h-3.5 text-[#FF0033]" />{PHONE}</a>
          <div className="flex items-center gap-3">
            <Link to="/track" data-testid="topbar-track" className="text-slate-400 hover:text-white flex items-center gap-1">
              <Truck className="w-3.5 h-3.5" /><span className="hidden sm:inline">Urmărește comanda</span>
            </Link>
            <Link to="/account" data-testid="topbar-account" className="text-slate-400 hover:text-white flex items-center gap-1">
              <User className="w-3.5 h-3.5" /><span className="hidden sm:inline">Contul meu</span>
            </Link>
            <div className="flex items-center gap-0.5">
              {["ro", "en", "fr"].map(l => (
                <button key={l} data-testid={`lang-${l}`} onClick={() => setLang(l)} className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase transition-colors ${lang === l ? "bg-[#FF0033] text-white" : "text-slate-500 hover:bg-white/5"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <BFHero maxDiscount={maxDiscount} logo={LOGO_URL} onCta={() => document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" })}>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <Input
            data-testid="boutique-search"
            placeholder={t.search_placeholder}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-12 h-12 text-base bg-[#13131A] border-[#262636] text-white placeholder:text-slate-500 rounded-sm focus:border-[#FF0033] focus-visible:ring-[#FF0033]/40"
          />
        </div>
      </BFHero>

      {/* Category discount tiles */}
      <div className="max-w-7xl mx-auto px-4 pt-6">
        <h2 className={`${DISPLAY} font-black text-3xl sm:text-4xl text-white mb-4`}>{bf ? <>Reduceri pe <span className="text-[#FF0033]">categorii</span></> : "Categorii"}</h2>
        <CategoryDiscountTiles types={SCOOTER_TYPES} scooters={scooters} discounts={catDiscounts} active={activeTab === "scooters" ? filterType : null}
          onSelect={v => { setActiveTab("scooters"); setFilterType(v); document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" }); }} />
      </div>

      {/* Tabs */}
      <div id="shop" className="bg-[#0A0A0E]/95 backdrop-blur-xl border-b border-[#262636] sticky top-0 z-30 mt-8">
        <div className="max-w-7xl mx-auto px-2 sm:px-4">
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-none">
            {[
              { id: "scooters", icon: <Star className="w-4 h-4" />, label: t.tab_scooters, count: filteredScooters.length },
              { id: "parts", icon: <Wrench className="w-4 h-4" />, label: t.tab_parts, count: filteredParts.length },
              { id: "casse", icon: <Package className="w-4 h-4" />, label: t.tab_casse, count: casse.length },
            ].map(tab => (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => { setActiveTab(tab.id); resetFilters(); }}
                className={`${DISPLAY} flex items-center gap-1.5 px-3 sm:px-5 py-3 text-base sm:text-lg font-extrabold border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${activeTab === tab.id ? "border-[#FF0033] text-white" : "border-transparent text-slate-500 hover:text-slate-300"}`}
              >
                {tab.icon} {tab.label}
                <span className="bg-white/5 text-slate-400 px-1.5 py-0.5 rounded-sm text-[10px] font-['Russo_One','Exo_2'] [font-synthesis:none]">{tab.count}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-5">
        {activeTab !== "casse" ? (
          <div className="space-y-4">
            {activeTab === "scooters" && (
              <RecentlyViewed ids={recentIds} scooters={scooters} onOpen={openDetail} onClear={() => { localStorage.removeItem("scutersh:recent"); setRecentIds([]); }} />
            )}

            {/* Category Buttons - Scooters */}
            {activeTab === "scooters" && (
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="scooter-categories">
                <button
                  onClick={() => setFilterType("all")}
                  className={`flex-shrink-0 px-4 py-2 rounded-sm text-sm font-bold transition-colors ${filterType === "all" ? "bg-[#FF0033] text-white" : "bg-[#13131A] text-slate-400 hover:bg-[#1A1A24] border border-[#262636]"}`}
                  data-testid="cat-all"
                >
                  Toate ({scooters.length})
                </button>
                {SCOOTER_TYPES.map(st => {
                  const count = scooters.filter(st.filter).length;
                  const pct = catDiscounts[st.value] || 0;
                  return (
                    <button
                      key={st.value}
                      onClick={() => setFilterType(filterType === st.value ? "all" : st.value)}
                      className={`flex-shrink-0 px-4 py-2 rounded-sm text-sm font-bold transition-colors whitespace-nowrap ${filterType === st.value ? "bg-[#FF0033] text-white" : "bg-[#13131A] text-slate-400 hover:bg-[#1A1A24] border border-[#262636]"}`}
                      data-testid={`cat-${st.value}`}
                    >
                      {st[lang]} ({count}){pct > 0 && <span className="ml-1.5 text-[#FFD700]">-{pct}%</span>}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Category Buttons - Parts */}
            {activeTab === "parts" && (
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="parts-categories">
                <button
                  onClick={() => setFilterCategory("all")}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-all ${filterCategory === "all" ? "bg-[#FF0033] text-white" : "bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10"}`}
                >
                  Toate ({parts.length})
                </button>
                {PART_CATEGORIES.map(c => {
                  const count = parts.filter(p => p.category === c.value).length;
                  if (count === 0) return null;
                  return (
                    <button
                      key={c.value}
                      onClick={() => setFilterCategory(filterCategory === c.value ? "all" : c.value)}
                      className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${filterCategory === c.value ? "bg-[#FF0033] text-white" : "bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10"}`}
                    >
                      {c[lang]} ({count})
                    </button>
                  );
                })}
                <button
                  onClick={() => setRequestModal(true)}
                  className="flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 transition-all whitespace-nowrap"
                  data-testid="btn-request-part"
                >
                  <span className="inline-flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5" /> {t.btn_request_part}</span>
                </button>
              </div>
            )}

            {/* Filter Bar */}
            <div className="flex flex-wrap items-center gap-2 bg-[#111113] rounded-xl border border-white/10 p-3">
              {/* Sort */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="h-8 w-auto min-w-[130px] text-xs bg-white/5 border-white/10" data-testid="sort-select">
                  <ArrowUpDown className="w-3 h-3 mr-1.5 text-gray-500" />
                  <SelectValue placeholder="Ordoneaza" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Aleatoriu</SelectItem>
                  <SelectItem value="price_asc">Pret crescator</SelectItem>
                  <SelectItem value="price_desc">Pret descrescator</SelectItem>
                  {activeTab === "scooters" && <SelectItem value="year_desc">An: Nou - Vechi</SelectItem>}
                  {activeTab === "scooters" && <SelectItem value="year_asc">An: Vechi - Nou</SelectItem>}
                  {activeTab === "scooters" && <SelectItem value="note">Nota tehnica</SelectItem>}
                </SelectContent>
              </Select>

              {/* Brand */}
              <Select value={filterBrand} onValueChange={setFilterBrand}>
                <SelectTrigger className="h-8 w-auto min-w-[110px] text-xs bg-white/5 border-white/10">
                  <SelectValue placeholder={t.filter_brand} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.filter_brand}: {t.filter_all}</SelectItem>
                  {brands.map(b => <SelectItem key={b} value={b}>{b.trim()}</SelectItem>)}
                </SelectContent>
              </Select>

              {/* Price */}
              <Select value={filterPriceRange} onValueChange={setFilterPriceRange}>
                <SelectTrigger className="h-8 w-auto min-w-[120px] text-xs bg-white/5 border-white/10">
                  <SelectValue placeholder={t.filter_price} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.filter_price}: {t.filter_all}</SelectItem>
                  {priceRanges.map(r => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                </SelectContent>
              </Select>

              {/* Active filter indicators + Reset */}
              {(filterBrand !== "all" || filterPriceRange !== "all" || filterType !== "all" || filterCategory !== "all" || sortBy !== "auto") && (
                <button onClick={resetFilters} className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1 ml-auto">
                  <X className="w-3 h-3" /> Reset
                </button>
              )}

              <span className="text-xs text-gray-500 ml-auto">{currentItems.length} {t.results}</span>
            </div>

            {/* BLACK FRIDAY Promotii Section */}
            {activeTab === "scooters" && scooters.filter(s => s.discount_percent > 0 && s.status !== "sold").length > 0 && (
              <div className="mb-6 bg-[#13131A] border border-[#FF0033]/40 rounded-lg p-4 sm:p-6 shadow-[0_0_40px_#FF003320]" data-testid="promo-section">
                <div className="flex flex-wrap items-end justify-between gap-2 mb-4">
                  <h3 className={`${DISPLAY} font-black text-3xl text-white flex items-center gap-2`}>
                    <Tag className="w-6 h-6 text-[#FF0033]" /> {bf ? <>Oferte <span className="text-[#FF0033]">Black Friday</span></> : "Oferte speciale"}
                  </h3>
                  <span data-testid="promo-urgency" className="text-xs font-bold text-[#FFD700] uppercase tracking-wider">Profitați de reduceri – stoc foarte limitat!</span>
                </div>
                <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-thin">
                  {scooters.filter(s => s.discount_percent > 0 && s.status !== "sold").map(s => (
                    <div key={`promo-${s.scooter_id}`} className="flex-shrink-0 w-64 bg-gradient-to-b from-red-500/20 to-black/50 border border-red-500/30 rounded-xl overflow-hidden cursor-pointer hover:border-red-400/60 hover:shadow-lg hover:shadow-red-500/10 transition-all" onClick={() => openDetail(s, "scooter")}>
                      <div className="aspect-[4/3] relative bg-black">
                        {s.photos?.[0] ? (
                          <img src={s.photos[0]} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Package className="w-10 h-10 text-gray-600" /></div>
                        )}
                        <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-red-600/90 to-transparent p-2">
                          <div className="flex items-center justify-between">
                            <span className="bg-black/60 text-red-400 px-2 py-0.5 rounded text-[10px] font-black">{bf ? "BLACK FRIDAY" : "PROMO"}</span>
                            <span className="bg-red-500 text-white px-2 py-0.5 rounded text-xs font-bold">-{s.discount_percent}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="p-3">
                        <p className="font-semibold text-sm text-white truncate">{s.brand} {s.model}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-400 line-through">{s.old_price} EUR</span>
                          <span className="text-base font-bold text-red-400">{s.selling_price} EUR</span>
                        </div>
                        <div className="mt-1.5 text-[10px] text-red-400/70 font-medium">Economisesti {(s.old_price || 0) - (s.selling_price || 0)} EUR</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Product Grid */}
            {loading ? (
              <div className="text-center py-20 text-gray-500">Loading...</div>
            ) : currentItems.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-gray-500 text-lg">{t.no_results}</p>
                {activeTab === "parts" && (
                  <Button onClick={() => setRequestModal(true)} className="mt-4">{t.btn_request_part}</Button>
                )}
              </div>
            ) : activeTab === "scooters" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredScooters.map(s => <ScooterCard key={s.scooter_id} scooter={s} t={t} lang={lang} onOrder={() => handleOrder(s)} eurRon={eurRon} onReserve={handleReserve} onView={openDetail} />)}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {filteredParts.map(p => <PartCard key={p.part_id} part={p} t={t} lang={lang} onOrder={() => handleOrder(p, "part")} onRequest={() => setRequestModal(true)} eurRon={eurRon} onView={openDetail} />)}
              </div>
            )}
          </div>
        ) : activeTab === "casse" ? (
          /* Casse / Dezmembrari Section */
          <div className="space-y-6">
            {/* Banner Description */}
            <div className="bg-gradient-to-r from-orange-500/10 to-amber-500/5 border border-orange-500/20 rounded-xl p-6 text-center" data-testid="casse-banner">
              <h2 className="text-2xl font-bold text-white mb-3">Dezmembrari Scutere</h2>
              <p className="text-gray-300 text-sm max-w-2xl mx-auto leading-relaxed mb-4">
                Aceste scutere sunt disponibile pentru <strong className="text-orange-400">dezmembrare</strong>. Gasiti piesa de care aveti nevoie si faceti o cerere direct pe scuterul dorit. Va contactam prin <strong className="text-green-400">WhatsApp</strong> sau <strong className="text-emerald-400">telefon</strong> cu pretul si disponibilitatea.
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-400">
                <span className="flex items-center gap-1.5 bg-white/5 px-3 py-1.5 rounded-full"><Truck className="w-4 h-4 text-emerald-400" /> Livrare in toata Romania</span>
                <span className="flex items-center gap-1.5 bg-white/5 px-3 py-1.5 rounded-full"><Package className="w-4 h-4 text-emerald-400" /> Piese originale verificate</span>
                <span className="flex items-center gap-1.5 bg-white/5 px-3 py-1.5 rounded-full"><Phone className="w-4 h-4 text-emerald-400" /> Raspuns in 24h</span>
              </div>
            </div>

            {/* Search Bar for Casse */}
            <div className="flex items-center gap-3 bg-[#111113] rounded-xl border border-white/10 p-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  value={casseSearch}
                  onChange={e => setCasseSearch(e.target.value)}
                  placeholder="Cauta dupa marca, model, cilindree..."
                  className="pl-10 bg-white/5 border-white/10 text-sm"
                  data-testid="casse-search"
                />
              </div>
              <span className="text-xs text-gray-500 flex-shrink-0">{filteredCasse.length} / {casse.length}</span>
              {casseSearch && (
                <button onClick={() => setCasseSearch("")} className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1">
                  <X className="w-3 h-3" /> Reset
                </button>
              )}
            </div>

            {/* Casse Scooters Grid */}
            {filteredCasse.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredCasse.map(s => (
                  <CasseCard key={s.scooter_id} scooter={s} t={t} />
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">{casseSearch ? "Niciun rezultat gasit" : t.casse_no_models}</p>
            )}

            {/* General request form */}
            <div className="max-w-lg mx-auto bg-[#111113] rounded-xl border border-white/10 p-6">
              <h3 className="font-bold text-lg mb-2 text-center text-white">Nu gasesti scuterul tau?</h3>
              <p className="text-sm text-gray-400 text-center mb-4">Spune-ne ce piesa cauti si de la ce scuter, si iti cautam noi!</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">{t.request_brand}</Label><Input value={requestForm.scooter_brand} onChange={e => setRequestForm(f => ({ ...f, scooter_brand: e.target.value }))} /></div>
                <div><Label className="text-xs">{t.request_model}</Label><Input value={requestForm.scooter_model} onChange={e => setRequestForm(f => ({ ...f, scooter_model: e.target.value }))} /></div>
                <div><Label className="text-xs">{t.order_first_name}</Label><Input value={requestForm.first_name} onChange={e => setRequestForm(f => ({ ...f, first_name: e.target.value }))} /></div>
                <div><Label className="text-xs">{t.order_phone}</Label><Input value={requestForm.phone} onChange={e => setRequestForm(f => ({ ...f, phone: e.target.value }))} /></div>
              </div>
              <div className="mt-3"><Label className="text-xs">{t.request_part}</Label><Textarea value={requestForm.part_description} onChange={e => setRequestForm(f => ({ ...f, part_description: e.target.value }))} rows={3} placeholder="Descrieti piesa de care aveti nevoie..." /></div>
              <Button data-testid="submit-casse-request" onClick={submitPartRequest} className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700">{t.request_submit}</Button>
            </div>
          </div>
        ) : null}
      </div>

      {/* Product Detail Modal */}
      <Dialog open={!!detailItem} onOpenChange={o => { if (!o) closeDetail(); }}>
        <DialogContent className="font-['Exo_2'] max-w-3xl bg-[#111113] border-white/10 text-white p-0 max-h-[90vh] overflow-y-auto">
          {detailItem && <DetailModalContent
            item={detailItem}
            eurRon={eurRon}
            detailPhotoIndex={detailPhotoIndex}
            setDetailPhotoIndex={setDetailPhotoIndex}
            t={t}
            onOrder={() => { closeDetail(); handleOrder(detailItem, detailItem._type === "scooter" ? "scooter" : "part"); }}
            onReserve={() => { closeDetail(); handleReserve(detailItem); }}
            onClose={closeDetail}
          />}
        </DialogContent>
      </Dialog>

      {/* Order Modal */}
      <Dialog open={!!orderModal} onOpenChange={() => setOrderModal(null)}>
        <DialogContent className="font-['Exo_2'] max-w-md bg-[#111113] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">{t.order_title}</DialogTitle>
          </DialogHeader>
          {orderModal && (
            <div className="space-y-3">
              <div className="bg-white/5 rounded-lg p-3 flex items-center justify-between">
                <span className="font-semibold text-sm">{orderModal.item_name}</span>
                <div className="text-right">
                  <Badge className="bg-emerald-600 text-white font-bold">{orderModal.price} EUR</Badge>
                  {eurRon && <p className="text-[10px] text-gray-400 mt-0.5">~{Math.round(orderModal.price * eurRon)} RON</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-gray-400">{t.order_first_name} *</Label><Input data-testid="order-first-name" value={orderForm.first_name} onChange={e => setOrderForm(f => ({ ...f, first_name: e.target.value }))} className="bg-white/5 border-white/10" /></div>
                <div><Label className="text-xs text-gray-400">{t.order_last_name} *</Label><Input data-testid="order-last-name" value={orderForm.last_name} onChange={e => setOrderForm(f => ({ ...f, last_name: e.target.value }))} className="bg-white/5 border-white/10" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-gray-400">{t.order_phone} *</Label><Input data-testid="order-phone" value={orderForm.phone} onChange={e => setOrderForm(f => ({ ...f, phone: e.target.value }))} className="bg-white/5 border-white/10" /></div>
                <div><Label className="text-xs text-gray-400">{t.order_email}</Label><Input value={orderForm.email} onChange={e => setOrderForm(f => ({ ...f, email: e.target.value }))} className="bg-white/5 border-white/10" /></div>
              </div>
              <div><Label className="text-xs text-gray-400">{t.order_address}</Label><Input value={orderForm.delivery_address} onChange={e => setOrderForm(f => ({ ...f, delivery_address: e.target.value }))} className="bg-white/5 border-white/10" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-gray-400">{t.order_city}</Label><Input value={orderForm.city} onChange={e => setOrderForm(f => ({ ...f, city: e.target.value }))} className="bg-white/5 border-white/10" /></div>
                <div>
                  <Label className="text-xs text-gray-400">Zona livrare</Label>
                  <Select value={orderForm.zone_id} onValueChange={v => setOrderForm(f => ({ ...f, zone_id: v }))}>
                    <SelectTrigger className="h-9 text-sm bg-white/5 border-white/10" data-testid="order-zone"><SelectValue placeholder="Alege zona" /></SelectTrigger>
                    <SelectContent>
                      {zones.map(z => <SelectItem key={z.zone_id} value={z.zone_id}>{z.zone_name} ({z.price_eur} EUR)</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-xs text-gray-400">{t.order_notes}</Label><Textarea value={orderForm.notes} onChange={e => setOrderForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="bg-white/5 border-white/10" /></div>
              <Button data-testid="submit-order" onClick={submitOrder} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">{t.order_submit}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Order Success Modal */}
      <Dialog open={!!lastOrderResult} onOpenChange={() => setLastOrderResult(null)}>
        <DialogContent className="font-['Exo_2'] max-w-sm bg-[#111113] border-white/10 text-white text-center">
          <div className="py-4 space-y-4">
            <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto">
              <ShoppingCart className="w-8 h-8 text-white" />
            </div>
            <h3 className="font-bold text-xl">{t.order_success}</h3>
            {lastOrderResult && (
              <>
                <div className="bg-white/5 rounded-lg p-4 space-y-2">
                  <p className="text-sm text-gray-400">Cod tracking:</p>
                  <p className="font-mono font-bold text-2xl text-emerald-400" data-testid="order-tracking-code">{lastOrderResult.tracking_code}</p>
                  <p className="text-sm text-gray-400">Total: <span className="text-white font-bold">{lastOrderResult.total_eur} EUR</span>
                    {lastOrderResult.delivery_fee_eur > 0 && <span className="text-gray-500"> (livrare: {lastOrderResult.delivery_fee_eur} EUR)</span>}
                  </p>
                </div>
                <Link to={`/track`} className="inline-block">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="go-track-order">
                    <Truck className="w-4 h-4 mr-2" /> Urmareste comanda
                  </Button>
                </Link>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Part Request Modal */}
      <Dialog open={requestModal} onOpenChange={setRequestModal}>
        <DialogContent className="font-['Exo_2'] max-w-md bg-[#111113] border-white/10 text-white">
          <DialogHeader><DialogTitle className="text-white">{t.request_title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-gray-400">{t.order_first_name} *</Label><Input value={requestForm.first_name} onChange={e => setRequestForm(f => ({ ...f, first_name: e.target.value }))} className="bg-white/5 border-white/10" /></div>
              <div><Label className="text-xs text-gray-400">{t.order_phone} *</Label><Input value={requestForm.phone} onChange={e => setRequestForm(f => ({ ...f, phone: e.target.value }))} className="bg-white/5 border-white/10" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs text-gray-400">{t.request_brand}</Label><Input value={requestForm.scooter_brand} onChange={e => setRequestForm(f => ({ ...f, scooter_brand: e.target.value }))} className="bg-white/5 border-white/10" /></div>
              <div><Label className="text-xs text-gray-400">{t.request_model}</Label><Input value={requestForm.scooter_model} onChange={e => setRequestForm(f => ({ ...f, scooter_model: e.target.value }))} className="bg-white/5 border-white/10" /></div>
              <div><Label className="text-xs text-gray-400">{t.request_year}</Label><Input value={requestForm.scooter_year} onChange={e => setRequestForm(f => ({ ...f, scooter_year: e.target.value }))} className="bg-white/5 border-white/10" /></div>
            </div>
            <div><Label className="text-xs text-gray-400">{t.request_part} *</Label><Textarea value={requestForm.part_description} onChange={e => setRequestForm(f => ({ ...f, part_description: e.target.value }))} rows={3} className="bg-white/5 border-white/10" /></div>
            <Button data-testid="submit-part-request" onClick={submitPartRequest} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">{t.request_submit}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reservation Modal */}
      <Dialog open={!!reserveModal} onOpenChange={() => setReserveModal(null)}>
        <DialogContent className="font-['Exo_2'] max-w-md bg-[#111113] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CalendarCheck className="w-5 h-5 text-yellow-400" /> {t.reserve_title}
            </DialogTitle>
          </DialogHeader>
          {reserveModal && (
            <div className="space-y-3">
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-center justify-between">
                <span className="font-semibold text-sm">{reserveModal.brand} {reserveModal.model} {reserveModal.year}</span>
                <div className="text-right">
                  <Badge className="bg-emerald-600 text-white font-bold">{reserveModal.selling_price} EUR</Badge>
                  {eurRon && <p className="text-[10px] text-gray-400 mt-0.5">~{Math.round(reserveModal.selling_price * eurRon)} RON</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-gray-400">{t.order_first_name} *</Label><Input data-testid="reserve-first-name" value={reserveForm.first_name} onChange={e => setReserveForm(f => ({ ...f, first_name: e.target.value }))} className="bg-white/5 border-white/10" /></div>
                <div><Label className="text-xs text-gray-400">{t.order_last_name} *</Label><Input data-testid="reserve-last-name" value={reserveForm.last_name} onChange={e => setReserveForm(f => ({ ...f, last_name: e.target.value }))} className="bg-white/5 border-white/10" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-gray-400">{t.order_phone} *</Label><Input data-testid="reserve-phone" value={reserveForm.phone} onChange={e => setReserveForm(f => ({ ...f, phone: e.target.value }))} className="bg-white/5 border-white/10" /></div>
                <div><Label className="text-xs text-gray-400">{t.order_email}</Label><Input value={reserveForm.email} onChange={e => setReserveForm(f => ({ ...f, email: e.target.value }))} className="bg-white/5 border-white/10" /></div>
              </div>
              <div><Label className="text-xs text-gray-400">{t.order_notes}</Label><Textarea value={reserveForm.notes} onChange={e => setReserveForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="bg-white/5 border-white/10" /></div>
              <Button data-testid="submit-reservation" onClick={submitReservation} className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold">{t.reserve_submit}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reservation Success Modal */}
      <Dialog open={!!lastReserveResult} onOpenChange={() => setLastReserveResult(null)}>
        <DialogContent className="font-['Exo_2'] max-w-sm bg-[#111113] border-white/10 text-white text-center">
          <div className="py-4 space-y-4">
            <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto">
              <CalendarCheck className="w-8 h-8 text-black" />
            </div>
            <h3 className="font-bold text-xl">{t.reserve_success}</h3>
            {lastReserveResult && (
              <div className="bg-white/5 rounded-lg p-4 space-y-2">
                <p className="text-sm text-gray-400">Cod rezervare:</p>
                <p className="font-mono font-bold text-2xl text-yellow-400" data-testid="reservation-code">{lastReserveResult.reservation_id}</p>
                <p className="font-medium text-white">{lastReserveResult.scooter_name}</p>
                <p className="text-sm text-gray-400">Pret: <span className="text-white font-bold">{lastReserveResult.price} EUR</span></p>
              </div>
            )}
            <p className="text-xs text-gray-500">Va contactam in curand pentru confirmare.</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Floating Button */}
      <a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-float"
         className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white w-14 h-14 rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-transform">
        <MessageCircle className="w-7 h-7" />
      </a>

      {bf && <BFLifestyle />}

      {/* Footer */}
      <footer className="bg-[#0A0A0E] border-t border-[#262636] py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <img src={LOGO_URL} alt="Scutersh" className="w-10 h-10 rounded object-contain" />
            <p className="font-bold text-lg text-white">SCUTERSH</p>
          </div>
          <p className="text-gray-500 text-sm">{t.footer_text}</p>
          <div className="flex items-center justify-center gap-2 sm:gap-4 mt-3 text-xs sm:text-sm text-gray-500 flex-wrap">
            <span>{t.footer_address}</span>
            <span className="hidden sm:inline">|</span>
            <span>{PHONE}</span>
            <span className="hidden sm:inline">|</span>
            <a href={`mailto:${EMAIL}`} className="hover:text-red-400 transition-colors">{EMAIL}</a>
          </div>
          <div className="mt-4">
            <Link to="/track" className="text-[#FF0033] hover:text-white text-sm transition-colors">
              <Truck className="w-4 h-4 inline mr-1" />Urmareste comanda
            </Link>
          </div>
          <p className="text-gray-700 text-xs mt-4">&copy; {new Date().getFullYear()} Scutersh</p>
          <Link to="/carte" data-testid="footer-business-card" className="inline-block mt-2 text-[11px] text-gray-600 hover:text-gray-300 transition-colors">Digital Business Card</Link>
        </div>
      </footer>

      {/* AI Chat Widget */}
      <AIChatWidget />
    </div>
  );
}
